import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  forwardRef,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import moment from 'moment';
import { ConversionDateTimeService } from '../../services/conversion/conversion-datetime.service';
import { DateTimePickerDialogComponent } from './components/date-time-picker-dialog.component';

@Component({
  selector: 'app-date-time-picker',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    ReactiveFormsModule,
  ],
  templateUrl: './app-date-time-picker.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppDateTimePickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AppDateTimePickerComponent),
      multi: true,
    },
  ],
})
export class AppDateTimePickerComponent
  implements ControlValueAccessor, OnInit, OnChanges, Validator
{
  @Input() minDate?: Date;
  @Input() required = false;
  @Input() label = '';

  @Output() dateTimeSelected = new EventEmitter<string>();

  control = new FormControl<string>('', []);
  private dialog = inject(MatDialog);
  private internalUpdate = false;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange?: () => void;
  constructor(private conversionDateTimeService: ConversionDateTimeService) {}

  ngOnInit() {
    if (this.required) this.control.addValidators(Validators.required);

    // 🔹 When internal control status changes (valid/invalid), revalidate parent
    this.control.statusChanges.subscribe(() => {
      if (this.onValidatorChange) this.onValidatorChange();
    });
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  ngOnChanges(_: SimpleChanges): void {}

  // 🔹 CVA: write value into the form control
  writeValue(value: string | null): void {
    if (this.internalUpdate) return;

    const m = moment(value, moment.ISO_8601, true);
    const formatted = m.isValid()
      ? this.conversionDateTimeService.convertDateTime(m.toDate())
      : '';

    this.control.setValue(formatted, { emitEvent: false });
  }

  validate(control: AbstractControl): ValidationErrors | null {
  // Merge required + internal errors correctly
  const errors = this.control.errors || {};

  // Handle required explicitly
  if (this.required && !this.control.value) {
    errors['required'] = true;
  }

  const date = this.parseInputToDate(this.control.value)

  if (date && this.minDate && date < this.minDate) {
    this.control.setErrors({ minDate: true });
    this.control.markAsTouched();
    this.control.markAsDirty();
    errors['minDate'] = {
      requiredMin: this.minDate.toISOString(),
      actual: date.toISOString(),
    };
  }

  return Object.keys(errors).length ? errors : null;
}


  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.control.disable() : this.control.enable();
  }

  // 🔹 Manual user input
  onManualInput(event: Event) {
    const inputValue = (event.target as HTMLInputElement).value.trim();
    const date = this.parseInputToDate(inputValue);

    if (!date) {
      this.control.setErrors({ invalidFormat: true });
      return;
    }

    this.control.setErrors(null);
    const iso = date.toISOString();

    this.internalUpdate = true;
    this.onChange(iso);
    this.dateTimeSelected.emit(iso);
    this.internalUpdate = false;
  }

  onInputBlur() {
    this.onTouched();
  }

  // 🔹 Open dialog picker
  openDateTimePicker() {
    const initialDate = this.parseInputToDate(this.control.value);
    const dialogRef = this.dialog.open(DateTimePickerDialogComponent, {
      width: '450px',
      data: {
        selectedDate: initialDate ? initialDate.toISOString() : null,
        minDate: this.minDate,
      },
    });

    dialogRef.afterClosed().subscribe((result: Date | null) => {
      if (!result) return;

      const iso = result.toISOString();
      const formatted = this.conversionDateTimeService.convertDateTime(result);

      this.internalUpdate = true;
      this.control.setValue(formatted, { emitEvent: false });
      this.onChange(iso);
      this.dateTimeSelected.emit(iso);
      this.onTouched();
      this.internalUpdate = false;
    });
  }

  expectedFormatText(): string {
    return `${this.conversionDateTimeService.preferredDateFormat} ${this.conversionDateTimeService.preferredTimeFormat}`;
  }

  // 🔹 Convert “Today 8pm” / ISO / formatted date
  private parseInputToDate(value: unknown): Date | null {
    if (!value) return null;

    const fmtDate = this.conversionDateTimeService.preferredDateFormat;
    const fmtTime = this.conversionDateTimeService.preferredTimeFormat;

    if (value instanceof Date) return value;
    if (typeof value === 'string') {
      // ISO string
      if (moment(value, moment.ISO_8601, true).isValid())
        return moment(value).toDate();

      // "Today 8:30pm"
      const todayRegex =
        /^today\s*(?:at\s*)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i;
      const match = value.match(todayRegex);
      if (match) {
        let [, hourStr, minuteStr, meridian] = match;
        let hour = parseInt(hourStr, 10);
        const minute = minuteStr ? parseInt(minuteStr, 10) : 0;

        if (meridian) {
          meridian = meridian.toLowerCase();
          if (meridian === 'pm' && hour < 12) hour += 12;
          if (meridian === 'am' && hour === 12) hour = 0;
        } else if (hour < 8) {
          hour += 12; // assume PM for short inputs
        }

        return moment()
          .set({ hour, minute, second: 0, millisecond: 0 })
          .toDate();
      }

      // Strict format
      const m = moment(value, `${fmtDate} ${fmtTime}`, true);
      if (m.isValid()) return m.toDate();
    }

    return null;
  }

  formattedMinDate(){
    return this.conversionDateTimeService.convertDateTime(this.minDate)
  }
}
